import { IconFont, SignTitle } from '@/components'
import { Button, Drawer, Form, Input, Radio, Switch, Typography, Upload } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { t } from '@/i18n'
import Scrollbars from 'react-custom-scrollbars'
import { Message } from '@/components/message'
import { useGetGlobalParam, useUpdateGlobalParam } from '../../../utils'

const { useForm } = Form

interface ConfigureProps {
  configureVisible: boolean
  setConfigureVisible: React.Dispatch<React.SetStateAction<boolean>>
}

export type ParamsProps = {
  key: string
  description: string
  default: string
  type: string
  value: string
  is_show?: number
  is_permanent: number | string
  file_path: string
  limit_info?: string
  hasChange?: boolean
  options?: { label: string; value: string }[]
}

// 参数类型常量
const PARAM_TYPES = {
  INT: 'int',
  FLOAT: 'float',
  BOOL: 'bool',
  PASSWORD: 'password',
  FILE: 'file',
  STRING: 'string',
} as const

// 参数渲染组件
interface ParamInputProps {
  item: ParamsProps
  index: number
  paramType: 'Common' | 'File' | 'Command'
  value: string
  onChange: (value: string, hasChange?: boolean) => void
  disabled?: boolean
  uploadProps?: any
}

const ParamInput: React.FC<ParamInputProps> = ({
  item,
  index,
  paramType,
  value,
  onChange,
  disabled = false,
  uploadProps,
}) => {
  const fieldName = `${index}${paramType}Value`
  // 获取验证规则
  const getValidationRules = useCallback((paramItem: ParamsProps) => {
    const baseRules: any[] = []
    // 对于布尔类型，不需要字符串长度和必填验证
    if (paramItem.type === PARAM_TYPES.BOOL) {
      return baseRules
    }
    // 对于其他类型，添加基础验证
    baseRules.push(
      { max: 1024, message: t('参数最多为1024字') },
      { required: true, message: t('请输入参数') },
    )
    if (paramItem.type === PARAM_TYPES.INT || paramItem.type === PARAM_TYPES.FLOAT) {
      const limitInfo = paramItem.limit_info ? JSON.parse(paramItem.limit_info) : {}
      const { min, max } = limitInfo
      baseRules.push({
        validator: async (_: any, val: string) => {
          if (!val) return
          if (paramItem.type === PARAM_TYPES.INT) {
            if (!/^[0-9]+$/.test(val)) {
              throw new Error(t('参数仅支持数字'))
            }
            const numValue = Number(val)
            if (min !== undefined && numValue < min) {
              throw new Error(t('最小值为') + min)
            }
            if (max !== undefined && numValue > max) {
              throw new Error(t('最大值为') + max)
            }
          } else if (paramItem.type === PARAM_TYPES.FLOAT) {
            if (!/^(\d+)(\.\d+)?$/.test(val)) {
              throw new Error(t('参数仅支持数字和小数'))
            }
            const numValue = Number(val)
            if (min !== undefined && numValue < min) {
              throw new Error(t('最小值为') + min)
            }
            if (max !== undefined && numValue > max) {
              throw new Error(t('最大值为') + max)
            }
          }
        },
      })
    }
    return baseRules
  }, [])

  // 渲染不同类型的输入组件
  const renderInput = () => {
    switch (item.type) {
      case PARAM_TYPES.PASSWORD:
        return (
          <Input
            style={{ width: 400 }}
            type="password"
            onFocus={() => onChange('', true)}
            onBlur={() => {
              if (!value) {
                onChange('●●●●●●●●')
              }
            }}
            onChange={e => onChange(e.target.value, true)}
          />
        )
      case PARAM_TYPES.FILE:
        return (
          <div>
            <Upload
              name="file"
              action={`${
                process.env.NODE_ENV === 'dev' ? '/api' : ''
              }/portal/verify/endpointSecurity/uploadParamFile`}
              {...uploadProps}
              showUploadList={false}
              maxCount={1}
              onChange={({ file }) => {
                if (file.status === 'done') {
                  const { code, data } = file.response
                  if (code === 200) {
                    onChange(data?.name || '', false)
                  }
                } else if (file.status === 'error') {
                  Message.error(t('上传失败'))
                }
              }}
              disabled={disabled}
            >
              <Button type="primary">{t('上传文件')}</Button>
            </Upload>
            {value && value !== '●●●●●●●●' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
                <IconFont type="icon-fujian2" />
                <Typography.Text
                  style={{ marginRight: 36, maxWidth: 240 }}
                  ellipsis={{ tooltip: { getPopupContainer: () => document.body } }}
                >
                  {value}
                </Typography.Text>
                <IconFont
                  type="icon-shanchu-m"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onChange('', false)}
                />
              </div>
            )}
          </div>
        )
      case PARAM_TYPES.BOOL:
        if (item.key === 'rans_exec_needed') {
          return (
            <Switch
              checkedChildren={t('开')}
              unCheckedChildren={t('关')}
              onChange={checked => {
                onChange(checked.toString(), false)
                Message.success(checked ? t('参数已启用') : t('参数已停用'))
              }}
            />
          )
        }
        return null
      default:
        return (
          <Input
            style={{ width: 400 }}
            disabled={disabled}
            onChange={e => onChange(e.target.value, false)}
          />
        )
    }
  }

  return (
    <Form.Item
      name={fieldName}
      rules={getValidationRules(item)}
      validateFirst
      valuePropName={item.type === PARAM_TYPES.BOOL ? 'checked' : 'value'}
    >
      {renderInput()}
    </Form.Item>
  )
}

// 参数组组件
interface ParamGroupProps {
  title: string
  params: ParamsProps[]
  paramType: 'Common' | 'File' | 'Command'
  onParamChange: (index: number, value: string, hasChange?: boolean) => void
  windowsKey?: { [key: number]: { visible: false; key: number } }
  uploadProps?: any
}

const ParamGroup: React.FC<ParamGroupProps> = ({
  title,
  params,
  paramType,
  onParamChange,
  windowsKey,
  uploadProps,
}) => {
  if (!params?.length) return null

  return (
    <>
      <div
        className="flex"
        style={{ marginBottom: 8, justifyContent: 'space-between', alignItems: 'flex-end' }}
      >
        <div className="title">{title}</div>
      </div>
      <div className="params-deploy">
        {title === t('通用参数') && (
          <div className="params-flex">
            <Typography.Text
              style={{ marginTop: 6 }}
              ellipsis={{
                tooltip: { getPopupContainer: () => document.body },
                suffix: '：',
              }}
            >
              {t('设备检测方式')}
            </Typography.Text>
            <div style={{ display: 'flex', gap: 4, marginBottom: 24, marginLeft: 12 }}>
              <Form.Item name="detection_method" noStyle initialValue={'1'}>
                <Radio.Group>
                  <Radio value="1">{t('实时检测')}</Radio>
                </Radio.Group>
              </Form.Item>
            </div>
          </div>
        )}
        {params.map((item, index) => (
          <div className="params-flex" key={`${paramType}-${index}`}>
            <Typography.Text
              className="params-title"
              ellipsis={{
                tooltip: { getPopupContainer: () => document.body },
                suffix: item.key === 'exec_rate' ? '：' : '：',
              }}
            >
              {item.key === 'exec_rate'
                ? t('执行速率')
                : item.key === 'detection_method'
                ? t('设备检测方式')
                : item.key}
            </Typography.Text>
            <div className="content">
              <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                <ParamInput
                  item={item}
                  index={index}
                  paramType={paramType}
                  value={item.value}
                  onChange={(value, hasChange) => onParamChange(index, value, hasChange)}
                  disabled={item.key === 'evilDir'}
                  uploadProps={uploadProps}
                />
              </div>
              <Typography.Paragraph key={windowsKey?.[index]?.key || null} className="describe">
                {item.description}
              </Typography.Paragraph>
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

export const Configure: React.FC<ConfigureProps> = ({ configureVisible, setConfigureVisible }) => {
  // 用户数据
  const userData = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('token') || '{}')
    } catch {
      return {}
    }
  }, [])

  const [form] = useForm()

  const [finalCommonParams, setFinalCommonParams] = useState<ParamsProps[]>([])
  const [finalFileParams, setFinalFileParams] = useState<ParamsProps[]>([])
  const [finalCommandExecParams, setFinalCommandExecParams] = useState<ParamsProps[]>([])
  const [windowsKey, setWindowsKey] = useState<{ [key: number]: { visible: false; key: number } }>(
    {},
  )
  const [status, setStatus] = useState(true)
  const [getGlobalParam] = useGetGlobalParam()
  const [updateGlobalParam] = useUpdateGlobalParam()

  // 上传配置
  const uploadProps = useMemo(
    () => ({
      headers: {
        token: userData.token,
        'user-id': userData.id,
      },
      showUploadList: false,
    }),
    [userData.token, userData.id],
  )

  // 参数更新处理
  const handleParamChange = useCallback(
    (paramType: 'Common' | 'File' | 'Command', index: number, value: string, hasChange = false) => {
      const setterMap = {
        Common: setFinalCommonParams,
        File: setFinalFileParams,
        Command: setFinalCommandExecParams,
      }
      const setter = setterMap[paramType]
      setter(prev => {
        const newParams = [...prev]
        newParams[index] = {
          ...newParams[index],
          value,
          hasChange: hasChange || newParams[index].hasChange,
          is_permanent: newParams[index].is_permanent === 1 ? newParams[index].is_permanent : 2,
        }
        return newParams
      })
      if (status) {
        setStatus(false)
      }
    },
    [status],
  )

  useEffect(() => {
    if (!configureVisible) return
    const initializeData = async () => {
      try {
        const res = await getGlobalParam()
        form.resetFields()
        // 过滤掉检测方式和timeout单位相关的参数
        const commonParamsData = (
          res?.data?.global_param_group?.ParamTypeCommon?.params || []
        ).filter(item => item.key !== 'detection_method' && item.key !== 'timeout_unit')
        const fileParamsData =
          res?.data?.global_param_group?.ParamTypeFile?.params.filter(
            item => item.key !== 'exec_rate',
          ) || []
        const commandExecParamsData =
          res?.data?.global_param_group?.ParamTypeCommandExec?.params?.map(item => ({
            ...item,
            value: item.type === PARAM_TYPES.PASSWORD ? '●●●●●●●●' : item.value,
          })) || []
        setFinalCommonParams(commonParamsData)
        setFinalFileParams(fileParamsData)
        setFinalCommandExecParams(commandExecParamsData)
        // 设置表单初始值
        const formValues: Record<string, any> = {}
        ;[commonParamsData, fileParamsData, commandExecParamsData].forEach((params, groupIndex) => {
          const typeMap = ['Common', 'File', 'Command']
          params.forEach((item, index) => {
            const fieldName = `${index}${typeMap[groupIndex]}Value`
            formValues[fieldName] =
              item.type === PARAM_TYPES.BOOL ? item.value === 'true' : item.value
          })
        })
        form.setFieldsValue(formValues)
      } catch (error) {
        console.error('Failed to initialize data:', error)
        Message.error(t('初始化数据失败'))
      }
    }
    initializeData()
  }, [configureVisible, getGlobalParam, form])

  // 提交处理
  const handleSubmit = useCallback(async () => {
    try {
      const totalParams = [...finalCommonParams, ...finalFileParams, ...finalCommandExecParams]
      const formData = totalParams.map(item => {
        if (item.type === PARAM_TYPES.PASSWORD) {
          return item.hasChange
            ? { ...item, param_type: undefined, sort: undefined, limit_info: undefined }
            : {
                ...item,
                value: '',
                default: '',
                param_type: undefined,
                sort: undefined,
                limit_info: undefined,
              }
        }
        return { ...item, param_type: undefined, sort: undefined, limit_info: undefined }
      })
      await updateGlobalParam({
        param: formData,
      })
      Message.success(t('配置更新成功'))
      setConfigureVisible(false)
      setWindowsKey({})
      setStatus(true)
    } catch (error) {
      console.error('Failed to update configuration:', error)
      Message.error(t('配置更新失败'))
    }
  }, [
    finalCommonParams,
    finalFileParams,
    finalCommandExecParams,
    updateGlobalParam,
    setConfigureVisible,
  ])

  // 关闭处理
  const handleClose = useCallback(() => {
    setConfigureVisible(false)
    setWindowsKey({})
    setStatus(true)
    form.resetFields()
  }, [setConfigureVisible, form])

  return (
    <Container
      zIndex={1002}
      visible={configureVisible}
      width={600}
      destroyOnClose
      onClose={handleClose}
      getContainer={() => document.body}
      title={<SignTitle show>{t('全局参数配置')}</SignTitle>}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button onClick={handleClose}>{t('取消')}</Button>
          <Button type="primary" onClick={() => form.submit()}>
            {t('确定')}
          </Button>
        </div>
      }
    >
      <Scrollbars>
        <Form form={form} onFinish={handleSubmit} style={{ paddingRight: 24, overflowX: 'hidden' }}>
          <ParamGroup
            title={t('通用参数')}
            params={finalCommonParams}
            paramType="Common"
            onParamChange={(index, value, hasChange) =>
              handleParamChange('Common', index, value, hasChange)
            }
            windowsKey={windowsKey}
            uploadProps={uploadProps}
          />

          <ParamGroup
            title={t('文件落地用例')}
            params={finalFileParams}
            paramType="File"
            onParamChange={(index, value, hasChange) =>
              handleParamChange('File', index, value, hasChange)
            }
            windowsKey={windowsKey}
            uploadProps={uploadProps}
          />

          <ParamGroup
            title={t('命令执行用例')}
            params={finalCommandExecParams}
            paramType="Command"
            onParamChange={(index, value, hasChange) =>
              handleParamChange('Command', index, value, hasChange)
            }
            windowsKey={windowsKey}
            uploadProps={uploadProps}
          />
        </Form>
      </Scrollbars>
    </Container>
  )
}

const Container = styled(Drawer)`
  .ant-drawer-close {
    width: 36px;
    height: 36px;
    background: #d8d6f4;
    box-shadow: -1px 4px 10px 0px #c7c7c7;
    border-radius: 6px;
    border: none;
    margin-right: 0px;
    color: #4238c9;
    font-size: 16px;
    font-weight: 500;
    position: absolute;
    right: 0px;
    top: 0px;
    transition: transform 0.3s ease;

    &:hover {
      transform: translate(-10px, 8px);
      box-shadow: 0 0 0 0;
    }

    .ant-drawer-close-x {
      width: 100%;
      height: 100%;
      line-height: 36px;
      text-align: center;
    }
  }

  .ant-drawer-header {
    height: 56px;
    background: #f2f0ff;
    border-bottom: none;
    margin: 0;
  }

  .ant-drawer-body {
    padding-right: 0;
  }
  .flex {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    .text {
      width: calc(100% - 80px);
    }
  }
  .title {
    color: #333333;
    font-weight: 600;
  }
  .params-deploy {
    width: 100%;
    border-radius: 6px;
    padding: 10px;
    .os {
      margin-bottom: 10px;
    }
    .params-flex {
      display: flex;
    }
    .params-title {
      height: 32px;
      line-height: 32px;
      width: 106px;
    }
    .content {
      width: calc(100% - 110px);
      margin-left: 4px;
      word-wrap: break-word;
      word-break: break-all;
    }
  }
  .ant-typography-expand,
  .collapse {
    font-size: 12px;
    margin-left: 4px;
    color: #1a2b51;
    cursor: pointer;
  }
  .describe {
    color: #999;
    font-size: 12px;
    margin-bottom: 16px;
  }
  .describe-content {
    width: 442px;
    background: #f8f9fb;
    border-radius: 6px;
    padding: 10px;
    margin-top: 8px;
  }
  .ant-form-item {
    margin-bottom: 4px;
  }
  .ant-checkbox + span {
    padding-right: 0;
    padding-left: 4px;
  }

  .ant-radio-wrapper.ant-radio-wrapper-in-form-item {
    margin-right: 60px;
  }
`
